import { Box } from '@fullstory/components';
import { Notification } from '@fullstory/proto/fs/services/pinguin';
import { timestamp } from '@fullstory/proto/google/protobuf';
import { useLocalStorage } from '@fullstory/react-hooks';
import React, { useCallback, useMemo, useState } from 'react';
import { NotificationPopover } from '../../components/NotificationListV2/NotificationPopover';
import css from './index.module.scss';
import { fullstorySelectors, useFetchAllNotifications } from '@fullstory/redux';
import { shallowEqual, useSelector } from 'react-redux';
import {
  isAutoDismissable,
  maxTimestamp,
  userDismiss,
  getNotificationsToDisplay,
  groupedConfig,
} from './utils';
import { NotificationsSidecar } from '../NotificationsSidecar';
import { getLastMonthUnreadNotifications } from '@fullstory/redux/modules/notifications/selectors';

const { getOrgId, getUserInfo, getSeatRole } = fullstorySelectors;

/*
  A map of notification types to timestamps. We need to save the most recent timestamp
  for auto-dismissed notifications. Additionally, we need an entry for each
  notification trigger that must be manually dismissed.
*/
type NotificationTimestamps = {
  default: timestamp | null;
  triggers: {
    [key: string]: timestamp | null;
  };
};

/*
 The NotificationPopoverList container polls for notifications on a schedule and
 displays new notifications to the user as popovers.
*/
export const NotificationPopoverList: React.FC<{}> = () => {
  useFetchAllNotifications(true);
  const notifications = useSelector(getLastMonthUnreadNotifications, shallowEqual);

  const [showSidecar, setShowSidecar] = useState(false);
  const openSideCar = () => setShowSidecar(true);

  const orgId = useSelector(getOrgId);
  const userId = useSelector(getUserInfo).email;
  const userRole = useSelector(getSeatRole);

  const defaultTimestamps: NotificationTimestamps = {
    default: null,
    triggers: {},
  };
  userDismiss.forEach(trigger => (defaultTimestamps.triggers[trigger] = null));

  const [timestamps, setTimestamps] = useLocalStorage(
    `${userId}-${orgId}-notificationTimestamps`,
    defaultTimestamps,
  );

  // Filter to only notifications that occurred after the appropriate timestamp from local storage.
  // All others will have already been displayed to the user in a popover.
  const filteredNotifications = useMemo(
    () =>
      notifications.filter(n => {
        if (!n.createdAt) return true;
        const triggerId = n.configId?.triggerId ?? '';

        if (!isAutoDismissable(n, userRole)) {
          const timestamp = timestamps.triggers[triggerId];
          return !timestamp || n.createdAt > timestamp;
        }

        return !timestamps.default || n.createdAt > timestamps.default;
      }),
    [notifications, timestamps.default, timestamps.triggers, userRole],
  );

  const groupedPopovers = useMemo(
    () => getNotificationsToDisplay(filteredNotifications, userRole),
    [filteredNotifications, userRole],
  );

  // When dismissed, update the appropriate timestamp in local storage so we don't show that
  // notification as a popover again
  const onDismiss = useCallback(
    (notification: Notification) => {
      const triggerId = notification.configId?.triggerId ?? '';
      if (!isAutoDismissable(notification, userRole)) {
        setTimestamps(value => ({
          ...value,
          triggers: {
            ...value.triggers,
            [triggerId]: maxTimestamp(notification.createdAt, value.triggers[triggerId]),
          },
        }));
      } else {
        setTimestamps(value => ({
          ...value,
          default: maxTimestamp(notification.createdAt, value.default),
        }));
      }
    },
    [setTimestamps, userRole],
  );

  const onClickCta = (notification: Notification) => {
    if (notification.configId == groupedConfig) {
      openSideCar();
    }
    onDismiss(notification);
  };

  return (
    <>
      <Box className={css.NotificationPopoverList}>
        {groupedPopovers.map(notification => (
          <NotificationPopover
            notification={notification}
            key={notification.id}
            onDismiss={onDismiss}
            autoDismiss={isAutoDismissable(notification, userRole)}
            onClickCta={() => onClickCta(notification)}
          />
        ))}
      </Box>
      <NotificationsSidecar onClose={() => setShowSidecar(false)} open={showSidecar} />
    </>
  );
};
